import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { getCapabilities, getMediaDevices } from 'devices/utils';

import {
  DeviceKind,
  DeviceOutcome,
  PermissionStatus,
  acquireDevice,
  defaultAcquireDeps,
  isDeviceKindEnumerated,
  queryBrowserPermission,
} from '../core';
import {
  selectCameraPermission,
  selectIsCameraAvailable,
  selectIsMicrophoneAvailable,
  selectMicrophonePermission,
} from './devices.selectors';
import { devicePermissionResolved } from './devices.slice';

import type { AppDispatch, RootState } from 'store';

// The concrete states resolution can land on — a subset of PermissionStatus: it never returns
// 'unknown' (it resolves away from it) or 'unsupported' (handled before this runs).
type ResolvedPermission = Exclude<PermissionStatus, 'unknown' | 'unsupported'>;

/**
 * Fresh permission + availability snapshot read from the store *after* resolution dispatches,
 * so a caller awaiting this thunk reads post-dispatch values directly rather than a stale
 * React-render snapshot (see `useDevices.refreshPermissions`).
 */
export type DevicePermissionSnapshot = {
  microphone: PermissionStatus;
  camera: PermissionStatus;
  microphoneAvailable: boolean;
  cameraAvailable: boolean;
};

/**
 * Determine the ambient permission for a device kind WITHOUT triggering a browser prompt.
 * Uses the Permissions API when available, falling back to an `enumerateDevices` label
 * heuristic for Safari < 16. When the browser reports `granted` we additionally verify
 * OS-level access (which never prompts) so a macOS/Windows privacy block surfaces as
 * `system_denied` rather than a false `granted`.
 *
 * Moved here from `user/state` as the final step of ADR 0001: `devices/` now owns
 * resolution end-to-end and writes the devices slice directly.
 */
/**
 * OS-level verify of an already browser-`granted` permission, routed through the single
 * `acquireDevice` primitive. `acquireDevice` opens+disposes a stream (never prompts once granted)
 * and classifies the result. Treat `Granted` and `DeviceUnavailable` as "OS access held": the
 * browser already reported this permission `granted`, so a transient device glitch (busy / aborted
 * / unknown → DeviceUnavailable) is a usability blip, not a permission block. Only a real
 * permission/system denial (NotAllowedError → PermissionDenied/SystemDenied) counts as denied.
 */
const verifyOsAccess = async (kind: DeviceKind): Promise<boolean> => {
  const outcome = await acquireDevice(kind, defaultAcquireDeps(getCapabilities()));
  return outcome === DeviceOutcome.Granted || outcome === DeviceOutcome.DeviceUnavailable;
};

const resolveDevicePermission = async (kind: DeviceKind): Promise<ResolvedPermission> => {
  const browserState = await queryBrowserPermission(kind);
  const deviceKind = kind === 'microphone' ? 'audioinput' : 'videoinput';

  switch (browserState) {
    case 'granted': {
      // Permission is held. Only verify OS-level access when a device of this kind actually
      // exists — with none connected, getUserMedia fails with NotFoundError purely for lack of
      // hardware, which would mislabel a genuine grant as system_denied.
      //
      // Presence MUST come from the raw enumerate (isDeviceKindEnumerated), not the deviceId-filtered
      // getMediaDevices(): Firefox returns entries with a `kind` but an EMPTY `deviceId` for a granted
      // origin that holds no live capture track, and getMediaDevices() drops those (filter('deviceId')).
      // Keying off the filtered list made Firefox look device-less, so we returned 'granted' WITHOUT
      // opening a stream — leaving enumerateDevices() permanently blank (the empty-pickers bug). The raw
      // check keeps a present-but-hidden Firefox device visible, so verifyOsAccess (acquireDevice) runs:
      // it verifies OS access AND primes the session so the next enumerate exposes real deviceIds/labels.
      const hasDeviceOfKind = await isDeviceKindEnumerated(kind);
      if (!hasDeviceOfKind) {
        return 'granted';
      }

      const hasAccess = await verifyOsAccess(kind);
      if (!hasAccess) {
        logger.warn(`[resolveDevicePermission] ${kind} denied at OS level`);
        monitoring.trackAction(`devices.${kind}.system_permission_denied`);
      }
      return hasAccess ? 'granted' : 'system_denied';
    }

    case 'denied':
      return 'denied';

    case 'prompt':
      return 'prompt';

    default: {
      // Permissions API unavailable (Safari < 16): infer from whether device labels are visible
      // (labels are only exposed once permission was granted, so getUserMedia won't prompt).
      const devices = await getMediaDevices();
      const hasLabeledDevices = devices.some((d) => d.kind === deviceKind && d.label);

      if (hasLabeledDevices) {
        const hasAccess = await verifyOsAccess(kind);
        if (!hasAccess) {
          logger.warn(`[resolveDevicePermission] ${kind} denied (Permissions API unavailable)`);
          monitoring.trackAction(`devices.${kind}.permission_denied_on_verification`);
        }
        return hasAccess ? 'granted' : 'system_denied';
      }

      // No labels and no Permissions API — can't determine without prompting.
      return 'prompt';
    }
  }
};

// Dedup guards: two rapid resolve dispatches (initial load + an immediate devicechange) can both
// read 'unknown' from the same stale snapshot; the second awaits the first's in-flight promise
// instead of opening hardware twice. Module-level because the race spans separate thunk calls.
let micResolving: Promise<ResolvedPermission> | null = null;
let camResolving: Promise<ResolvedPermission> | null = null;

type ResolveOptions = { audioOnly?: boolean; force?: boolean; kinds?: DeviceKind[] } | undefined;
type ThunkApi = { state: RootState; dispatch: AppDispatch };

// The kinds a resolve call should touch. Explicit `kinds` wins (a consumer that only needs the
// microphone resolves ONLY the microphone — so it never fires a camera OS-verify, which on
// Firefox one-time permissions would surface a surprise camera prompt on a mic-only step).
// Falls back to the legacy `audioOnly` behaviour (mic always; camera unless audioOnly).
const kindsToResolve = (options: ResolveOptions): DeviceKind[] =>
  options?.kinds ?? (options?.audioOnly ? ['microphone'] : ['microphone', 'camera']);

/**
 * Resolve ambient permission for the relevant kinds (microphone always; camera unless
 * `audioOnly`) and write the result into the devices slice. Resolution runs once per kind —
 * only while the slice permission is still `'unknown'`; later calls are no-ops, so this is safe
 * to dispatch on mount and on every `devicechange`.
 */
export const resolveDevicePermissions = createAsyncThunk<DevicePermissionSnapshot, ResolveOptions, ThunkApi>(
  '[devices] resolve permissions',
  async (options, { dispatch, getState }) => {
    const state = getState();

    // A forced refresh must re-resolve from scratch — not piggyback on an in-flight non-forced
    // resolution whose result predates the change we are re-verifying for. Only clear a kind we will
    // actually re-resolve below: clearing camResolving under `audioOnly` would orphan an in-flight
    // camera resolution (no new one starts), leaving camera permission stuck at 'unknown'.
    const targetKinds = kindsToResolve(options);

    if (options?.force) {
      if (targetKinds.includes('microphone')) micResolving = null;
      if (targetKinds.includes('camera')) camResolving = null;
    }

    const resolveMic =
      targetKinds.includes('microphone') && (options?.force || selectMicrophonePermission(state) === 'unknown');
    const resolveCam =
      targetKinds.includes('camera') && (options?.force || selectCameraPermission(state) === 'unknown');

    if (resolveMic) {
      if (!micResolving) micResolving = resolveDevicePermission('microphone');
      try {
        dispatch(devicePermissionResolved({ kind: 'microphone', status: await micResolving }));
      } finally {
        micResolving = null;
      }
    }

    if (resolveCam) {
      if (!camResolving) camResolving = resolveDevicePermission('camera');
      try {
        dispatch(devicePermissionResolved({ kind: 'camera', status: await camResolving }));
      } finally {
        camResolving = null;
      }
    }

    // Read fresh, post-dispatch values so the caller never sees a stale React-render snapshot.
    const after = getState();
    return {
      microphone: selectMicrophonePermission(after),
      camera: selectCameraPermission(after),
      microphoneAvailable: selectIsMicrophoneAvailable(after),
      cameraAvailable: selectIsCameraAvailable(after),
    };
  }
);
